// CustomerTable (Domain/Customers, BL-189; was the table Customers.tsx drew itself, pages/ux-full
// screens/customers.js): All customers' answer, one row each. The customer's name is the row's way in
// (a button, underlined faintly, in ink on hover) with their number and home or business under it;
// then their phone, the address, the plan they're on, what they owe (for those who see it), what's open
// and the last visit. A head on the ink rule, rows on hairlines; owing that's overdue is red, in words.
// Five rows, then Show all on asking (DESIGN.md §3); a new answer starts at five again (the page keys it).
// While the next answer is on its way, the rows stay and a muted line says so ("Searching…"). The words
// come ready to show (bp-customers-model.ts customerTableRows). The table scrolls in its own box when
// it's wider than the page. Reads only --custable-*; Link reads its own.
import { useState } from 'react';
import { Link } from '../Link/Link.tsx';
import './CustomerTable.css';

export interface CustomerTableRow {
  id: string;
  name: string;
  /** Under the name: their number, and home or business when it's known ("C-1042, home"). */
  meta: string;
  phone?: string;
  /** "1287 Fairview Rd, Victoria", or the city alone. */
  address?: string;
  /** The plan they're on; null when they aren't a member. */
  member: string | null;
  /** What they owe, for those who see it ("$412.50"); null when nothing. */
  owes?: { amount: string; overdue: boolean } | null;
  /** What's open with them ("2 jobs (one under way), 1 estimate"). */
  open?: string;
  /** Their last visit's day ("Oct 21, 2026"). */
  lastVisit?: string;
}

export interface CustomerTableProps {
  rows: readonly CustomerTableRow[];
  /** Opens the customer's record. */
  onOpen: (id: string) => void;
  /** The Owes column: only for those who are told what people owe. */
  showOwes?: boolean;
  /** How many rows before Show all (5). */
  first?: number;
  /** The next answer is on its way: the rows stay, and this line says so ("Searching…"). */
  busyText?: string;
}

export function CustomerTable({ rows, onOpen, showOwes = false, first = 5, busyText }: CustomerTableProps) {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, first);
  return (
    <div className="fl-custable">
      {rows.length ? (
        <div className="fl-custable__wrap">
          <table className="fl-custable__t">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Member</th>
                {showOwes ? <th data-num="">Owes</th> : null}
                <th>Open</th>
                <th>Last visit</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id}>
                  <td data-name="">
                    <button type="button" className="fl-custable__open" onClick={() => onOpen(c.id)}>
                      {c.name}
                    </button>
                    <span className="fl-custable__meta">{c.meta}</span>
                  </td>
                  <td data-phone="">{c.phone ?? ''}</td>
                  <td>{c.address ?? ''}</td>
                  <td>{c.member ?? <span className="fl-custable__muted">No</span>}</td>
                  {showOwes ? (
                    <td data-num="">
                      {c.owes ? (
                        <>
                          <b className="fl-custable__owes" data-bad={c.owes.overdue ? '' : undefined}>
                            {c.owes.amount}
                          </b>
                          {c.owes.overdue ? (
                            <span className="fl-custable__meta" data-bad="">
                              Overdue
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="fl-custable__muted">Nothing</span>
                      )}
                    </td>
                  ) : null}
                  <td>{c.open ?? ''}</td>
                  <td data-day="">{c.lastVisit ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {rows.length > shown.length ? (
        <p className="fl-custable__more">
          <Link onPress={() => setAll(true)}>Show all {rows.length}</Link>
        </p>
      ) : null}
      {busyText ? (
        <p className="fl-custable__busy" role="status">
          {busyText}
        </p>
      ) : null}
    </div>
  );
}
